import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// Inject org context header on every request
api.interceptors.request.use((config) => {
    const orgId = localStorage.getItem("bl_org_id");
    if (orgId) {
        config.headers["X-Org-Id"] = orgId;
    }
    return config;
});

/**
 * Build a full URL for a stored attachment (receipt / supporting doc) that
 * can be opened via a plain <a href> in a new tab. Custom headers like
 * `X-Org-Id` never travel through a new-tab navigation, so we append the
 * current org id as a query param — the backend honours `?org_id=` as a
 * fallback (see get_current_org in auth.py). Without this, multi-org users
 * hit "Attachment not found" because the server defaults to their first
 * membership rather than the org that actually owns the receipt.
 */
export function attachmentUrl(attachmentId) {
    if (!attachmentId) return "";
    const orgId = localStorage.getItem("bl_org_id");
    const qs = orgId ? `?org_id=${encodeURIComponent(orgId)}` : "";
    return `${API_BASE}/attachments/${attachmentId}${qs}`;
}

export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export const fmtMoney = (n, currency = "KYD") => {
    const num = Number(n || 0);
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "USD",
            maximumFractionDigits: 2,
        }).format(num);
    } catch {
        return `${currency} ${num.toFixed(2)}`;
    }
};

/**
 * Compact money format used on narrow viewports (KPI cards on mobile).
 * Uses Intl compact notation — 5,028,902.51 → "KYD 5.03M". Fine detail
 * is preserved in the caller's tooltip via the non-compact fmtMoney.
 * Under ~10,000 we render the full value so cents stay visible.
 */
export const fmtMoneyCompact = (n, currency = "KYD") => {
    const num = Number(n || 0);
    if (Math.abs(num) < 10_000) return fmtMoney(num, currency);
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "USD",
            notation: "compact",
            maximumFractionDigits: 2,
        }).format(num);
    } catch {
        return `${currency} ${num.toFixed(0)}`;
    }
};

export const fmtDate = (s) => {
    if (!s) return "—";
    try {
        const dateOnly = typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
        let d;
        if (dateOnly) {
            const [y, m, day] = s.split("-").map(Number);
            d = new Date(y, m - 1, day);
        } else {
            d = new Date(s);
        }
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return s;
    }
};

// Returns today's date as YYYY-MM-DD using the user's local timezone.
// Avoids the UTC shift bug that occurs with new Date().toISOString().slice(0,10).
export const todayLocal = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
